import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {SheetRangeImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetRangeImpl';
import {ColumnIndex, RowIndex} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {
    ISheetContext,
    ISheetLocation,
    ISheetRangeIterator,
    SheetLocationKey,
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {SheetId} from '@mintdata/mint-kit/api/models/Identifiers';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import bind from 'bind-decorator';

export class SheetRangeIteratorImpl implements ISheetRangeIterator
{
    private _rangeValues:Map<SheetLocationKey, IValue> = null;

    constructor(private _ctx:ISheetContext,
                public readonly sheetId:SheetId,
                public readonly from:ISheetLocation,
                public readonly to:ISheetLocation)
    {
    }

    applyToEachSlotInRange(consumer:(sheetLocation:ISheetLocation) => void):void
    {
        const numCells = this.numCells;

        for( let i = 0; i < numCells; i++ )
            consumer(this.getNthCellLocation(i));
    }

    public get numCells():number
    {
        return this.numRows * this.numColumns;
    }

    public get numColumns():number
    {
        return Math.abs(this.to.columnIndex - this.from.columnIndex) + 1;
    }

    public get numRows():number
    {
        return Math.abs(this.to.rowIndex - this.from.rowIndex) + 1;
    }

    getNthCellLocation(offset:number):ISheetLocation
    {
        const {row, col} = this._computeRowCol(offset);

        const x = Math.min(this.from.columnIndex, this.to.columnIndex) + col;
        const y = Math.min(this.from.rowIndex, this.to.rowIndex) + row;

        return new SheetLocationImpl(this.sheetId, x, y);
    }

    @bind
    getNthCellValue(offset:number):IValue
    {
        const {row, col} = this._computeRowCol(offset);
        return this._getValue(col, row);
    }

    @bind
    private _computeRowCol(offset:number):{row:number, col:number}
    {
        const row = Math.floor(offset / this.numColumns);
        const col = offset % this.numColumns;

        return {row: row, col: col};
    }

    getValueAtOffset(xOffset:number, yOffset:number):IValue
    {
        let value:IValue;

        if( xOffset > this.numColumns - 1 )
        {
            mk.error(area.FormulaEval, `Invalid xOffset ${xOffset}, numColumns: ${this.numColumns}`);
            value = EvalErrors.Ref;
        }
        else if( yOffset > this.numRows - 1 )
        {
            mk.error(area.FormulaEval, `Invalid yOffset ${yOffset}, numRows: ${this.numRows}`);
            value = EvalErrors.Ref;
        }
        else
        {
            const x = Math.min(this.from.columnIndex, this.to.columnIndex) + xOffset;
            const y = Math.min(this.from.rowIndex, this.to.rowIndex) + yOffset;

            value = this._getValue(y, x);
        }

        return value;
    }

    private _getValue(col:ColumnIndex, row:RowIndex):IValue
    {
        if (this._rangeValues == null)
        {
            this._rangeValues = this._ctx.getRangeValues(new SheetRangeImpl(this.sheetId, this.from, this.to));
        }

        return this._rangeValues.get(new SheetLocationImpl(this.sheetId, col, row).asKey());
    }
}
